
import React from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import Conversation from "@/components/messages/Conversation";
import ConversationWrapper from "@/components/messages/ConversationWrapper";
import { areUsersInConversation } from "@/services/messages/threadUtils";
import { useConversationData } from "@/hooks/messages/useConversationData";
import { useConversationCallbacks } from "@/hooks/messages/useConversationCallbacks";

interface ConversationContainerProps {
  userId: string;
  onBack: () => void;
}

const ConversationContainer: React.FC<ConversationContainerProps> = ({ userId, onBack }) => {
  const [searchParams] = useSearchParams();
  const reagentId = searchParams.get("reagentId") || undefined;
  const { user } = useAuth();
  
  // Use the extracted hook for conversation data management
  const {
    messages,
    username,
    reagentName,
    isLoading,
    hasMore,
    error,
    loadMore,
    refreshConversation,
    isRealtimeConnected
  } = useConversationData({
    userId,
    reagentId
  });
  
  // Use the extracted hook for conversation callback handling
  const { handleMessageSent } = useConversationCallbacks({
    refreshConversation
  });
  
  // Verify users can message each other
  const canMessage = user && areUsersInConversation(user.id, userId, reagentId);
  
  // Determine the title to display
  const conversationTitle = reagentName 
    ? `Re: ${reagentName}` 
    : username 
      ? `Conversation with ${username}` 
      : "Conversation";

  return (
    <ConversationWrapper
      isLoading={isLoading}
      error={error}
      canMessage={canMessage}
      onBack={onBack}
      onRefresh={refreshConversation}
    >
      <Conversation
        messages={messages}
        otherUserId={userId}
        otherUsername={username || "Unknown User"}
        reagentId={reagentId}
        reagentName={reagentName}
        conversationTitle={conversationTitle}
        isLoading={isLoading}
        hasMore={hasMore}
        onBack={onBack}
        onMessageSent={handleMessageSent}
        onLoadMore={loadMore}
        onRefresh={refreshConversation}
        isRealtimeConnected={isRealtimeConnected}
      />
    </ConversationWrapper>
  );
};

export default React.memo(ConversationContainer);
